import { CustomFieldType, fieldTypeStringToEnum, LoadingState } from '../model/model';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { FieldEditorStatefulWrapper } from '../shared-components/stateful/field-editor-stateful/FieldEditorStatefulWrapper';
import { GlobalContext } from '../shared-components/common/GlobalContext';
import { CepPageHeader, TBreadcrumbsItem } from '../shared-components/stateless/CepPageHeader';
import { PageWrapper } from '../shared-components/stateless/layout/PageWrapper';
import { sanitizeUserInput_contextId, sanitizeUserInput_fieldId, toIntNullSafe } from 'generated-common/util';
import { DTOCustomFieldAndContextAndDefaultOption, ErrorOrPayload, GET_CUSTOMFIELD_CONTEXT_WITH_DEFAULT_OPTION } from 'generated-common';
import { CepHeaderFieldDetails } from 'shared-components/stateless/CepHeaderFieldDetails';
import { PageLoadError } from 'components/ui/PageLoadError';

export const FieldEditorEditOptionsPage = () => {
  const { t, tWithIcon, invoke } = useContext(GlobalContext);
  const [loadingState, setLoadingState] = useState(LoadingState.NOT_LOADED);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [errorId, setErrorId] = useState<null | string>(null);

  const [fieldContextAndDefaultOption, setFieldContextAndDefaultOption] = useState<DTOCustomFieldAndContextAndDefaultOption | null>(null);

  let { fieldId, contextId } = useParams<{ fieldId: any; contextId: any }>();
  const fieldIdSanitized = sanitizeUserInput_fieldId(fieldId);
  const contextIdSanitized = sanitizeUserInput_contextId(contextId);

  const breadcrumbItems: TBreadcrumbsItem[] = [
    {
      href: '/fe/fields',
      i18n: 'user.list.breadcrumbTitle',
      params: [],
    },
    {
      href: '/fe/fields/:fieldId/contexts',
      i18n: 'user.listContext.breadcrumbTitle',
      params: [{ name: 'fieldId', value: fieldIdSanitized, type: 'path' }],
    },
    {
      href: '/fe/fields/:fieldId/contexts/:contextId',
      i18n: 'user.editOptions.breadcrumbTitle',
      params: [
        { name: 'fieldId', value: fieldIdSanitized, type: 'path' },
        { name: 'contextId', value: contextIdSanitized, type: 'path' },
      ],
    },
  ];

  useEffect(() => {
    setLoadingState(LoadingState.LOADING);
    invoke(GET_CUSTOMFIELD_CONTEXT_WITH_DEFAULT_OPTION, { fieldId: fieldIdSanitized, contextId: toIntNullSafe(contextIdSanitized) }).then((result: any) => {
      const typedResult: ErrorOrPayload<DTOCustomFieldAndContextAndDefaultOption> = result;
      if (typedResult.hasError) {
        setFieldContextAndDefaultOption(null);
        setLoadingState(LoadingState.FAILED);
        setHasError(true);
        setError(typedResult.errorMessage ? result.errorMessage : 'Unexpected error');
        setErrorId(typedResult.errorId);
      } else {
        setFieldContextAndDefaultOption(typedResult.payload);
        setLoadingState(LoadingState.LOADED);
        setHasError(false);
        setError(null);
        setErrorId(null);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldIdSanitized, contextIdSanitized]);

  return (
    <PageWrapper>
      <CepPageHeader
        useNavigate={useNavigate}
        pageTitle={fieldContextAndDefaultOption && fieldContextAndDefaultOption.field ? fieldContextAndDefaultOption.field.fieldName : hasError ? t('global.pageLoadFailErrorHeadline') : undefined}
        breadcrumbItems={breadcrumbItems}
        isLoading={loadingState === LoadingState.LOADING}
        helpUri={'/user-guide/field-editor/#options'}
        helpContent={
          <>
            <p>{t('fieldEditor.notice.paragraph1')}</p>
            <ul>
              <li>{t('fieldEditor.notice.paragraph2')}</li>
              <li>{t('fieldEditor.notice.paragraph3')}</li>
              <li>{t('fieldEditor.notice.paragraph4')}</li>
              <li>{tWithIcon('fieldEditor.notice.paragraph5')}</li>
            </ul>
          </>
        }
      ></CepPageHeader>
      {!hasError && (
        <CepHeaderFieldDetails
          isLoading={loadingState === LoadingState.LOADING}
          fieldType={fieldContextAndDefaultOption && fieldContextAndDefaultOption.field ? fieldTypeStringToEnum(fieldContextAndDefaultOption.field.fieldType) : CustomFieldType.SINGLESEELCT}
          fieldName={fieldContextAndDefaultOption && fieldContextAndDefaultOption.field ? fieldContextAndDefaultOption.field.fieldName : ''}
          fieldDescription={fieldContextAndDefaultOption && fieldContextAndDefaultOption.field ? fieldContextAndDefaultOption.field.fieldDescription : ''}
          contextName={fieldContextAndDefaultOption && fieldContextAndDefaultOption.context ? fieldContextAndDefaultOption && fieldContextAndDefaultOption.context.contextName : null}
          contextDescription={fieldContextAndDefaultOption && fieldContextAndDefaultOption.context ? fieldContextAndDefaultOption && fieldContextAndDefaultOption.context.contextDescription : null}
        ></CepHeaderFieldDetails>
      )}

      {loadingState !== LoadingState.LOADING && (
        <>
          {hasError ? (
            <PageLoadError errorMessage={error} invokeKey={GET_CUSTOMFIELD_CONTEXT_WITH_DEFAULT_OPTION} errorId={errorId ? errorId : 'ERRLDNG'} />
          ) : (
            <FieldEditorStatefulWrapper
              customFieldId={fieldIdSanitized}
              contextId={toIntNullSafe(contextIdSanitized)}
              fieldType={fieldContextAndDefaultOption && fieldContextAndDefaultOption.field ? fieldTypeStringToEnum(fieldContextAndDefaultOption.field.fieldType) : CustomFieldType.SINGLESEELCT}
              initialDefaultOption={fieldContextAndDefaultOption ? fieldContextAndDefaultOption.defaultOption : {}}
              authenticatedUserCapabilities={{ userAllowedToDeleteOptions: true /* FIXME: */ }}
            />
          )}
        </>
      )}
    </PageWrapper>
  );
};
